import React from 'react';
import styled from 'styled-components';
import { Cloud, AlertCircle } from 'lucide-react';
import { theme } from '../styles/theme';

const FormContainer = styled.div`
  max-width: 100%;
`;

const InfoBox = styled.div`
  display: flex;
  gap: ${theme.spacing[3]};
  padding: ${theme.spacing[4]};
  background: ${theme.colors.info[100]};
  border: 1px solid ${theme.colors.info[500]}20;
  border-radius: ${theme.borderRadius.md};
  margin-bottom: ${theme.spacing[6]};
`;

const InfoContent = styled.div`
  flex: 1;
`;

const InfoTitle = styled.h4`
  color: ${theme.colors.info[700]};
  margin-bottom: ${theme.spacing[1]};
  font-weight: ${theme.typography.fontWeight.semibold};
  font-size: ${theme.typography.fontSize.sm};
`;

const InfoText = styled.p`
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.textSecondary};
  line-height: ${theme.typography.lineHeight.relaxed};
`;

const ServicesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: ${theme.spacing[4]};
  margin-bottom: ${theme.spacing[6]};
`;

const ServiceCard = styled.div`
  position: relative;
  background: ${theme.colors.surface};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.lg};
  padding: ${theme.spacing[6]};
  text-align: center;
  transition: all ${theme.transitions.base};
  cursor: not-allowed;
  opacity: 0.7;
  
  &:hover {
    border-color: ${theme.colors.borderHover};
    box-shadow: ${theme.shadows.sm};
  }
`;

const ServiceIcon = styled.div`
  width: 64px;
  height: 64px;
  margin: 0 auto ${theme.spacing[4]};
  border-radius: ${theme.borderRadius.xl};
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  
  &.google-drive {
    background: linear-gradient(135deg, #4285F4 0%, #34A853 25%, #FBBC05 50%, #EA4335 75%);
  }
  
  &.onedrive-personal {
    background: linear-gradient(135deg, #0078D4 0%, #106EBE 100%);
  }
  
  &.onedrive-work {
    background: linear-gradient(135deg, #5A5A5A 0%, #3A3A3A 100%);
  }

  svg {
    width: 28px;
    height: 28px;
    color: white;
  }
`;

const ServiceTitle = styled.h3`
  font-size: ${theme.typography.fontSize.lg};
  font-weight: ${theme.typography.fontWeight.semibold};
  color: ${theme.colors.textPrimary};
  margin-bottom: ${theme.spacing[2]};
`;

const ServiceDescription = styled.p`
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.textSecondary};
  line-height: ${theme.typography.lineHeight.relaxed};
  margin-bottom: ${theme.spacing[3]};
`;

const ComingSoonBadge = styled.div`
  position: absolute;
  top: ${theme.spacing[3]};
  right: ${theme.spacing[3]};
  background: ${theme.colors.primary[500]};
  color: white;
  font-size: ${theme.typography.fontSize.xs};
  font-weight: ${theme.typography.fontWeight.semibold};
  padding: ${theme.spacing[1]} ${theme.spacing[2]};
  border-radius: ${theme.borderRadius.full};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const FeatureList = styled.ul`
  list-style: none;
  text-align: left;
  margin-top: ${theme.spacing[4]};
  
  li {
    font-size: ${theme.typography.fontSize.sm};
    color: ${theme.colors.textSecondary};
    padding: ${theme.spacing[1]} 0;
    position: relative;
    padding-left: ${theme.spacing[4]};
    
    &:before {
      content: '•';
      color: ${theme.colors.primary[500]};
      font-weight: bold;
      position: absolute;
      left: 0;
    }
  }
`;

const ComingSoonSection = styled.div`
  background: ${theme.colors.backgroundSecondary};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.md};
  padding: ${theme.spacing[6]};
  text-align: center;
  margin-top: ${theme.spacing[6]};
`;

const ComingSoonTitle = styled.h3`
  font-size: ${theme.typography.fontSize.xl};
  font-weight: ${theme.typography.fontWeight.semibold};
  color: ${theme.colors.textPrimary};
  margin-bottom: ${theme.spacing[3]};
`;

const ComingSoonText = styled.p`
  font-size: ${theme.typography.fontSize.base};
  color: ${theme.colors.textSecondary};
  line-height: ${theme.typography.lineHeight.relaxed};
  max-width: 500px;
  margin: 0 auto;
`;

const CloudAppsTab: React.FC = () => {
  return (
    <FormContainer>
      <InfoBox>
        <AlertCircle size={20} color={theme.colors.info[500]} />
        <InfoContent>
          <InfoTitle>Cloud Storage Integration</InfoTitle>
          <InfoText>
            Connect your cloud storage accounts to access tender documents directly from Google Drive, 
            OneDrive, and other services. Seamlessly import files without downloading them first.
          </InfoText>
        </InfoContent>
      </InfoBox>

      <ServicesGrid>
        <ServiceCard>
          <ComingSoonBadge>Coming Soon</ComingSoonBadge>
          <ServiceIcon className="google-drive">
            <Cloud />
          </ServiceIcon>
          <ServiceTitle>Google Drive</ServiceTitle>
          <ServiceDescription>
            Access documents directly from your Google Drive account
          </ServiceDescription>
          <FeatureList>
            <li>Browse folders and files</li>
            <li>Real-time sync</li>
            <li>Shared document access</li>
            <li>Version history support</li>
          </FeatureList>
        </ServiceCard>

        <ServiceCard>
          <ComingSoonBadge>Coming Soon</ComingSoonBadge>
          <ServiceIcon className="onedrive-personal">
            <Cloud />
          </ServiceIcon>
          <ServiceTitle>OneDrive Personal</ServiceTitle>
          <ServiceDescription>
            Import files from your personal Microsoft OneDrive account
          </ServiceDescription>
          <FeatureList>
            <li>Personal file access</li>
            <li>Office 365 integration</li>
            <li>Automatic sync</li>
            <li>File sharing support</li>
          </FeatureList>
        </ServiceCard>

        <ServiceCard>
          <ComingSoonBadge>Coming Soon</ComingSoonBadge>
          <ServiceIcon className="onedrive-work">
            <Cloud />
          </ServiceIcon>
          <ServiceTitle>OneDrive Work/School</ServiceTitle>
          <ServiceDescription>
            Connect to your organization's OneDrive for Business account
          </ServiceDescription>
          <FeatureList>
            <li>Enterprise security</li>
            <li>SharePoint integration</li>
            <li>Team collaboration</li>
            <li>Advanced permissions</li>
          </FeatureList>
        </ServiceCard>
      </ServicesGrid>

      <ComingSoonSection>
        <ComingSoonTitle>🚀 Exciting Features Ahead!</ComingSoonTitle>
        <ComingSoonText>
          We're working hard to bring you seamless cloud storage integration. Soon you'll be able to 
          connect your favorite cloud services and access your tender documents with just a few clicks. 
          Stay tuned for updates!
        </ComingSoonText>
      </ComingSoonSection>
    </FormContainer>
  );
};

export default CloudAppsTab;